import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Users, Activity, AlertTriangle, FileText, Layers } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

type Tab = "dashboard" | "health" | "errors" | "audit";

interface DashboardData {
  statistics: {
    users: { total: number; active: number };
    pages: { total: number; active: number };
    conversations: { total: number; active: number; today: number; thisWeek: number };
  };
  services: Record<string, { name: string; status: string }>;
}

interface PageHealth {
  pageId: string;
  name: string;
  owner: { id: string; name: string; email: string } | null;
  isConnected: boolean;
  isActive: boolean;
  tokenValid: boolean;
  webhook: { isSubscribed: boolean; lastWebhookReceived: string | null; stale: boolean; lastError: string | null };
  errors24h: number;
}

interface ErrorEvent {
  _id: string;
  eventType: string;
  pageId?: string;
  timestamp?: string;
  createdAt?: string;
  metadata?: any;
  details?: any;
}

interface AuditEvent {
  _id: string;
  adminId?: { _id: string; username: string; email: string; role: string } | null;
  action: string;
  targetType: string;
  targetId?: string;
  payload?: any;
  description?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("dashboard");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [pageHealth, setPageHealth] = useState<PageHealth[]>([]);
  const [errors, setErrors] = useState<ErrorEvent[]>([]);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [tabError, setTabError] = useState<string | null>(null);

  const getAuthHeader = useCallback((): Record<string, string> => {
    const token = localStorage.getItem("adminToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin/login");
  };

  const apiGet = useCallback(
    async (path: string) => {
      const res = await fetch(`${API_BASE_URL}${path}`, { headers: getAuthHeader() });
      if (res.status === 401) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        navigate("/admin/login");
        throw new Error("unauthorized");
      }
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    [getAuthHeader, navigate]
  );

  const fetchDashboard = useCallback(async () => {
    const data = await apiGet("/api/admin/dashboard");
    setDashboardData(data.dashboard);
  }, [apiGet]);

  const fetchHealth = useCallback(async () => {
    const data = await apiGet("/api/admin/pages/health");
    setPageHealth(data.pages || []);
  }, [apiGet]);

  const fetchErrors = useCallback(async () => {
    const data = await apiGet("/api/admin/errors?hours=24&limit=100");
    setErrors(data.events || []);
  }, [apiGet]);

  const fetchAudit = useCallback(async () => {
    const data = await apiGet("/api/admin/audit?limit=100");
    setAudit(data.events || []);
  }, [apiGet]);

  useEffect(() => {
    if (!localStorage.getItem("adminToken")) {
      navigate("/admin/login");
      return;
    }
    setLoading(true);
    fetchDashboard()
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [navigate, fetchDashboard]);

  useEffect(() => {
    setTabError(null);
    if (activeTab === "health") fetchHealth().catch((e) => setTabError(e.message));
    if (activeTab === "errors") fetchErrors().catch((e) => setTabError(e.message));
    if (activeTab === "audit") fetchAudit().catch((e) => setTabError(e.message));
  }, [activeTab, fetchHealth, fetchErrors, fetchAudit]);

  const formatDate = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : "—");

  if (loading) {
    return <div className="p-6 text-gray-500">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
            <Shield className="h-6 w-6" /> Admin Panel
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <TabButton tab="dashboard" active={activeTab} onClick={setActiveTab}>Dashboard</TabButton>
            <TabButton tab="health" active={activeTab} onClick={setActiveTab}><Activity className="inline h-4 w-4 mr-1" />Page Health</TabButton>
            <TabButton tab="errors" active={activeTab} onClick={setActiveTab}><AlertTriangle className="inline h-4 w-4 mr-1" />Errors</TabButton>
            <TabButton tab="audit" active={activeTab} onClick={setActiveTab}><FileText className="inline h-4 w-4 mr-1" />Audit</TabButton>
            <button
              onClick={() => navigate("/admin/pages")}
              className="px-3 py-1 rounded bg-white border"
              type="button"
            >
              <Layers className="inline h-4 w-4 mr-1" />
              Pages
            </button>
            <button
              onClick={() => navigate("/admin/users")}
              className="px-3 py-1 rounded bg-white border"
              type="button"
            >
              <Users className="inline h-4 w-4 mr-1" />
              Users
            </button>
            <button onClick={handleLogout} className="px-3 py-1 rounded bg-red-600 text-white">Logout</button>
          </div>
        </div>

        {tabError && (
          <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-800 text-sm">
            {tabError}
          </div>
        )}

        {activeTab === "dashboard" && dashboardData && (
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard label="Users" value={dashboardData.statistics.users.total} sub={`${dashboardData.statistics.users.active} active`} />
            <StatCard label="Pages" value={dashboardData.statistics.pages.total} sub={`${dashboardData.statistics.pages.active} connected`} />
            <StatCard label="Conversations" value={dashboardData.statistics.conversations.total} sub={`${dashboardData.statistics.conversations.today} today`} />
          </div>
        )}

        {activeTab === "health" && (
          <div className="bg-white rounded border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-2">Page</th>
                  <th className="p-2">Owner</th>
                  <th className="p-2">Connected</th>
                  <th className="p-2">Token</th>
                  <th className="p-2">Webhook</th>
                  <th className="p-2">Last Event</th>
                  <th className="p-2">Errors (24h)</th>
                </tr>
              </thead>
              <tbody>
                {pageHealth.map((p) => (
                  <tr key={p.pageId} className="border-t">
                    <td className="p-2">
                      <div className="font-medium text-gray-800">{p.name}</div>
                      <div className="text-xs text-gray-500">{p.pageId}</div>
                    </td>
                    <td className="p-2 text-xs text-gray-600">
                      {p.owner ? <>{p.owner.name}<br />{p.owner.email}</> : "—"}
                    </td>
                    <td className="p-2"><Pill ok={p.isConnected} good="Yes" bad="No" /></td>
                    <td className="p-2"><Pill ok={p.tokenValid} good="Valid" bad="Invalid" /></td>
                    <td className="p-2">
                      <Pill ok={p.webhook.isSubscribed && !p.webhook.stale} good="OK" bad={p.webhook.isSubscribed ? "Stale" : "Off"} />
                      {p.webhook.lastError && <div className="text-xs text-red-600 mt-1">{p.webhook.lastError}</div>}
                    </td>
                    <td className="p-2 text-xs text-gray-600">{formatDate(p.webhook.lastWebhookReceived)}</td>
                    <td className={`p-2 font-medium ${p.errors24h > 0 ? "text-red-600" : "text-gray-700"}`}>{p.errors24h}</td>
                  </tr>
                ))}
                {pageHealth.length === 0 && (
                  <tr><td className="p-4 text-center text-gray-500" colSpan={7}>No pages.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "errors" && (
          <div className="bg-white rounded border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-2">When</th>
                  <th className="p-2">Event</th>
                  <th className="p-2">Page</th>
                  <th className="p-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {errors.map((e) => (
                  <tr key={e._id} className="border-t align-top">
                    <td className="p-2 text-xs text-gray-600 whitespace-nowrap">{formatDate(e.timestamp || e.createdAt)}</td>
                    <td className="p-2 font-mono text-xs">{e.eventType}</td>
                    <td className="p-2 text-xs">{e.pageId || "—"}</td>
                    <td className="p-2 text-xs">
                      <pre className="bg-gray-50 rounded p-2 max-w-2xl overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(e.metadata || e.details || {}, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))}
                {errors.length === 0 && (
                  <tr><td className="p-4 text-center text-gray-500" colSpan={4}>No errors in the last 24h.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "audit" && (
          <div className="bg-white rounded border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-2">When</th>
                  <th className="p-2">Admin</th>
                  <th className="p-2">Action</th>
                  <th className="p-2">Target</th>
                  <th className="p-2">IP</th>
                  <th className="p-2">Payload</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a._id} className="border-t align-top">
                    <td className="p-2 text-xs text-gray-600 whitespace-nowrap">{formatDate(a.createdAt)}</td>
                    <td className="p-2 text-xs">{a.adminId ? `${a.adminId.username} (${a.adminId.role})` : "—"}</td>
                    <td className="p-2 font-mono text-xs">{a.action}</td>
                    <td className="p-2 text-xs">{a.targetType}{a.targetId ? `:${a.targetId}` : ""}</td>
                    <td className="p-2 text-xs">{a.ipAddress || "—"}</td>
                    <td className="p-2 text-xs">
                      <pre className="bg-gray-50 rounded p-2 max-w-2xl overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(a.payload || {}, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))}
                {audit.length === 0 && (
                  <tr><td className="p-4 text-center text-gray-500" colSpan={6}>No audit events.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const TabButton: React.FC<{ tab: Tab; active: Tab; onClick: (t: Tab) => void; children: React.ReactNode }> = ({ tab, active, onClick, children }) => (
  <button
    onClick={() => onClick(tab)}
    className={`px-3 py-1 rounded ${active === tab ? "bg-gray-900 text-white" : "bg-white border"}`}
    type="button"
  >
    {children}
  </button>
);

const StatCard: React.FC<{ label: string; value: number; sub?: string }> = ({ label, value, sub }) => (
  <div className="bg-white p-4 rounded border">
    <div className="text-sm text-gray-500">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const Pill: React.FC<{ ok: boolean; good: string; bad: string }> = ({ ok, good, bad }) => (
  <span className={`px-2 py-0.5 rounded text-xs ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
    {ok ? good : bad}
  </span>
);

export default AdminDashboard;
